import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { Helmet } from 'react-helmet';

const DEFAULT_IMAGE =
  'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_640.png';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePlans, setActivePlans] = useState([]);

  /* ===================== LOGGED-IN USER ===================== */
  const isLoggedIn = localStorage.getItem('user') !== null;
  const loggedInUser = isLoggedIn
    ? JSON.parse(localStorage.getItem('user'))
    : null;

  const loggedInUserType = loggedInUser?.user_type?.toLowerCase();
  const hasActivePlan = activePlans.some(
    (plan) => plan.user_id === loggedInUser?.id
  );

  /* ===================== DATA FETCH ===================== */
  useEffect(() => {
    fetchUserDetails();
    fetchActivePlans();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/user/user-details/${userId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch user details');
      }

      setUser(response.data.user);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to load user details'
      );
    } finally {
      setLoading(false);
    }
  };
  
    useEffect(() => {
  const handleContextMenu = (e) => e.preventDefault();
  const handleKeyDown = (e) => {
    if (e.key === "F12") e.preventDefault();
    if (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) e.preventDefault();
    if (e.ctrlKey && (e.key === "U" || e.key === "S")) e.preventDefault();
  };
  document.addEventListener("contextmenu", handleContextMenu);
  document.addEventListener("keydown", handleKeyDown);
  return () => {
    document.removeEventListener("contextmenu", handleContextMenu);
    document.removeEventListener("keydown", handleKeyDown);
  };
}, []);


  const fetchActivePlans = async () => {
    try {
      const response = await api.get('/user/active-plans');
      setActivePlans(response.data.plans || []);
    } catch {
      setActivePlans([]);
    }
  };

  /* ===================== PROTECTED FIELD HELPER ===================== */
const renderProtectedField = (value) => {
  if (!value) return 'N/A';

  const viewedUserType = basicInfo?.user_type?.toLowerCase();

  // Case 1: Viewing NON-partner profile → always visible
  if (viewedUserType !== 'partner') {
    return <span>{value}</span>;
  }

  // Case 2: Viewing PARTNER profile
  // Only PARTNER with ACTIVE PLAN can see
  if (loggedInUserType === 'partner' && hasActivePlan) {
    return <span>{value}</span>;
  }

  // All other cases → hide / blur
  return (
    <span style={{ filter: 'blur(3px)', userSelect: 'none' }}>
      {value}
    </span>
  );
};


  /* ===================== UTILS ===================== */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderSection = (title, data, fields) =>
    data &&
    Object.values(data).some((val) => val !== null && val !== '') && (
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">{title}</h5>
        </div>
        <div className="card-body">
          <div className="row">
            {fields.map(
              (field) =>
                data[field.key] && (
                  <div key={field.key} className="col-md-6 mb-3">
                    <strong>{field.label}:</strong>
                    <div className="text-muted">
                      {field.format
                        ? field.format(data[field.key])
                        : data[field.key]}
                    </div>
                  </div>
                )
            )}
          </div>
        </div>
      </div>
    );

  /* ===================== STATES ===================== */
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="text-center py-5">Loading user details...</div>
        <Footer />
      </>
    );
  }

  if (error || !user) {
    return (
      <>
        <Navbar />
        <div className="alert alert-danger text-center mt-5">
          {error || 'User not found'}
        </div>
        <Footer />
      </>
    );
  }

  const {
    basicInfo,
    contactInfo,
    personalInfo,
    categoryInfo,
    professionalInfo,
    businessInfo,
    employmentInfo,
    partnershipInfo,
  } = user;

  /* ===================== UI ===================== */
  return (
    <div className="user-detail-page">
      <Helmet>
        <title>{basicInfo.name} - User Details</title>
      </Helmet>

      <Navbar />

      <section className="section-padding section-bg">
        <div className="container">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h3>{basicInfo.name}</h3>

              <p className="text-muted">
                <i className="las la-user-tie me-2"></i>
                {basicInfo.user_type}
              </p>

              <p className="text-muted">
                <i className="las la-envelope me-2"></i>
                {renderProtectedField(contactInfo.email)}
              </p>

              <p className="text-muted">
                <i className="las la-phone me-2"></i>
                {renderProtectedField(contactInfo.contact_no)}
              </p>

           {basicInfo.user_type?.toLowerCase() === 'partner' &&
 !(loggedInUserType === 'partner' && hasActivePlan) && (
  <div className="alert alert-info mt-3">
    Only partners with an active plan can view partner contact details.
  </div>
)}

              <button
                onClick={() => navigate('/users')}
                className="btn btn-outline-primary mt-3"
              >
                Back to List
              </button>
            </div>
          </div>

          {renderSection('Basic Information', basicInfo, [
            { key: 'user_type', label: 'User Type' },
            {
              key: 'created_at',
              label: 'Joined Date',
              format: formatDate,
            },
          ])}

          {renderSection('Contact Information', contactInfo, [
            {
              key: 'email',
              label: 'Email',
              format: renderProtectedField,
            },
            {
              key: 'contact_no',
              label: 'Contact Number',
              format: renderProtectedField,
            },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State' },
            { key: 'pincode', label: 'Pincode' },
          ])}

          {renderSection('Personal Information', personalInfo, [
            { key: 'age', label: 'Age' },
            { key: 'gender', label: 'Gender' },
          ])}

          {renderSection('Professional Information', professionalInfo, [
            { key: 'skills', label: 'Skills' },
          ])}

          {businessInfo &&
            renderSection('Business Information', businessInfo, [
              { key: 'business_name', label: 'Business Name' },
            ])}

          {employmentInfo &&
            renderSection('Employment Information', employmentInfo, [
              { key: 'job_skills', label: 'Job Skills' },
            ])}

          {partnershipInfo &&
            renderSection('Partnership Information', partnershipInfo, [
              { key: 'future_vision', label: 'Future Vision' },
            ])}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UserDetail;
